
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { SearchService } from 'src/app/core/services/search/search.service';
import { EventData } from 'src/app/module/events/models/event.model';
import { EventsService } from 'src/app/module/events/services/events.service';
import { EventLoadType } from 'src/app/shared/enums/get-event.enum';
import { HelpChatService } from 'src/app/shared/services/help-chat/help-chat.service';

type ChatStep =
  | 'start'
  | 'faqAnswer'
  | 'supportType'
  | 'technical'
  | 'platformContact'
  | 'eventProblem'
  | 'eventPicker'
  | 'organizerContact';

interface HelpOption {
  label: string;
  description: string;
  icon: string;
  next: ChatStep;
}

interface QuickFaq {
  question: string;
  answer: string;
  icon: string;
}

interface OrganizerContact {
  eventName: string;
  organizerName?: string;
  email?: string;
  phone?: string;
}

@Component({
  selector: 'app-help-chat-widget',
  imports: [FormsModule],
  templateUrl: './help-chat-widget.component.html',
  styleUrls: ['./help-chat-widget.component.scss'],
})
export class HelpChatWidgetComponent {
  private readonly eventsService = inject(EventsService);
  private readonly searchService = inject(SearchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly helpChatService = inject(HelpChatService);

  readonly isOpen = this.helpChatService.isOpen;
  readonly step = signal<ChatStep>('start');
  readonly events = signal<EventData[]>([]);
  readonly eventSearch = signal('');
  readonly searchedTerm = signal('');
  readonly visibleEvents = computed(() => this.events().slice(0, this.eventLimit()));
  readonly eventLimit = signal(8);
  readonly loadingEvents = signal(false);
  readonly loadingContact = signal(false);
  readonly selectedContact = signal<OrganizerContact | null>(null);
  readonly selectedFaq = signal<QuickFaq | null>(null);

  readonly platformWhatsApp = 'https://api.whatsapp.com/send?phone=5534999469600&text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20a%20plataforma%20Seu%20Ingresso%20Aqui';
  readonly platformEmail = 'contato@seuingressoaqui.com';

  readonly quickFaqs: QuickFaq[] = [
    {
      question: 'Como compro ingressos na plataforma?',
      answer: 'Acesse o evento, selecione o tipo e a quantidade de ingressos, preencha seus dados, escolha a forma de pagamento e confirme a compra.',
      icon: 'pi pi-shopping-cart',
    },
    {
      question: 'Quais formas de pagamento são aceitas?',
      answer: 'A plataforma aceita cartão e PIX, conforme disponibilidade do evento e do intermediador de pagamento no momento da compra.',
      icon: 'pi pi-credit-card',
    },
    {
      question: 'Quando recebo meu ingresso?',
      answer: 'O ingresso digital é enviado após a confirmação do pagamento para o e-mail usado na compra. Confira também spam/lixo eletrônico e a área Meus Ingressos.',
      icon: 'pi pi-envelope',
    },
    {
      question: 'Como funciona o QR Code?',
      answer: 'O QR Code é único e deve ser apresentado na entrada do evento, na tela do celular ou impresso. Não compartilhe o código, pois ele é validado apenas uma vez.',
      icon: 'pi pi-qrcode',
    },
  ];

  readonly supportTypeOptions: HelpOption[] = [
    {
      label: 'Problema com o evento',
      description: 'Compra, pagamento, cancelamento, local, horário, atrações ou reembolso.',
      icon: 'pi pi-calendar-times',
      next: 'eventProblem',
    },
    {
      label: 'Problema técnico',
      description: 'Ingresso, login, cadastro, dados da conta ou erro no site.',
      icon: 'pi pi-cog',
      next: 'technical',
    },
  ];

  readonly technicalOptions: HelpOption[] = [
    {
      label: 'Meu ingresso',
      description: 'QR Code, e-mail não recebido ou meus ingressos.',
      icon: 'pi pi-qrcode',
      next: 'platformContact',
    },
    {
      label: 'Conta e acesso',
      description: 'Login, cadastro, senha ou dados do perfil.',
      icon: 'pi pi-user',
      next: 'platformContact',
    },
    {
      label: 'Erro no site',
      description: 'Tela travada, erro inesperado ou dificuldade de navegação.',
      icon: 'pi pi-exclamation-triangle',
      next: 'platformContact',
    },
  ];

  readonly eventOptions: HelpOption[] = [
    {
      label: 'Comprar ingresso',
      description: 'Ajuda para escolher ingresso, preencher dados e finalizar.',
      icon: 'pi pi-shopping-cart',
      next: 'eventPicker',
    },
    {
      label: 'Pagamento',
      description: 'PIX, cartão, cobrança, aprovação ou taxa.',
      icon: 'pi pi-credit-card',
      next: 'eventPicker',
    },
    {
      label: 'Cancelamento ou adiamento',
      description: 'A política é definida pelo organizador.',
      icon: 'pi pi-calendar-times',
      next: 'eventPicker',
    },
    {
      label: 'Local, horário ou programação',
      description: 'Informação operacional do evento.',
      icon: 'pi pi-map-marker',
      next: 'eventPicker',
    },
    {
      label: 'Reembolso do evento',
      description: 'A decisão final segue a regra do organizador.',
      icon: 'pi pi-refresh',
      next: 'eventPicker',
    },
  ];

  toggle(): void {
    this.helpChatService.toggle();
  }

  close(): void {
    this.helpChatService.close();
  }

  reset(): void {
    this.step.set('start');
    this.selectedContact.set(null);
    this.selectedFaq.set(null);
    this.eventLimit.set(8);
    this.eventSearch.set('');
  }

  openFaq(faq: QuickFaq): void {
    this.selectedFaq.set(faq);
    this.step.set('faqAnswer');
  }

  goTo(step: ChatStep): void {
    this.step.set(step);
    if (step === 'eventPicker' && this.events().length === 0) {
      this.loadEvents();
    }
    if (step === 'eventPicker') {
      this.eventLimit.set(8);
      this.eventSearch.set('');
      this.searchedTerm.set('');
    }
  }

  searchEvents(event?: Event): void {
    event?.preventDefault();

    const term = this.eventSearch().trim();
    this.eventLimit.set(8);
    this.searchedTerm.set(term);

    if (!term) {
      this.loadEvents();
      return;
    }

    this.loadingEvents.set(true);
    this.searchService.getSearchEvents(term, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of({ data: [] }))
      )
      .subscribe((response) => {
        this.events.set(response?.data || []);
        this.loadingEvents.set(false);
      });
  }

  showMoreEvents(): void {
    this.eventLimit.update((limit) => limit + 8);
  }

  selectEvent(event: EventData): void {
    this.loadingContact.set(true);
    this.selectedContact.set(null);

    this.eventsService.getEventById(event.slugUrl || event.id, EventLoadType.Summarized, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(event))
      )
      .subscribe((details: any) => {
        this.selectedContact.set({
          eventName: details?.name || event.name,
          organizerName: details?.nameOrganizer || event.nameOrganizer,
          email: details?.organizerEmail,
          phone: details?.organizerPhoneNumber,
        });
        this.loadingContact.set(false);
        this.step.set('organizerContact');
      });
  }

  formatEventMeta(event: EventData): string {
    const location = [event.city, event.state].filter(Boolean).join(' - ');
    return [event.startDate, location].filter(Boolean).join(' - ');
  }

  whatsappLink(phone?: string): string {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return '';
    const phoneWithCountry = digits.startsWith('55') ? digits : `55${digits}`;
    return `https://api.whatsapp.com/send?phone=${phoneWithCountry}&text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20sobre%20um%20evento`;
  }

  private loadEvents(): void {
    this.loadingEvents.set(true);
    this.eventsService.getAllEvents(1, 100, 1, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of({ data: [] }))
      )
      .subscribe((response: any) => {
        this.events.set(response?.data || []);
        this.loadingEvents.set(false);
      });
  }

}
